import { mergeAttributes, Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import {
  Bot,
  FileText,
  MessageSquareQuote,
  Puzzle,
  SearchCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Token kinds supported inside the chat composer editor.
 * Each token renders inline and behaves like an atomic editor node.
 */
export type ComposerTokenKind = "agent" | "comment" | "command" | "file" | "skill";

/**
 * Serializable token payload stored as node attributes.
 * These values are used both for rendering and for submit serialization.
 */
export interface ComposerTokenAttrs {
  /**
   * Semantic token kind.
   * Used to determine styling and downstream behavior.
   */
  kind: ComposerTokenKind;

  /**
   * Optional stable identifier for tokens backed by data entities.
   * Agent tokens use the real agent id so parent state can stay in sync.
   */
  id?: string | null;

  /**
   * User-facing token label rendered inline in the editor.
   * This is the text the user sees inside the chip.
   */
  label: string;

  /**
   * Optional hidden value used during submit serialization.
   * Comment tokens store the original note text here.
   */
  value?: string | null;

  /**
   * Small icon key used by the node view renderer.
   * Keeps node attrs serializable while still showing branded icons.
   */
  icon?: string | null;
}

function getTokenIcon(icon: string | null | undefined, kind: ComposerTokenKind): LucideIcon {
  if (icon === "message-square-quote") {
    return MessageSquareQuote;
  }

  if (icon === "search-check") {
    return SearchCheck;
  }

  if (icon === "file-text") {
    return FileText;
  }

  if (icon === "puzzle") {
    return Puzzle;
  }

  if (kind === "comment") {
    return MessageSquareQuote;
  }

  if (kind === "command") {
    return SearchCheck;
  }

  if (kind === "file") {
    return FileText;
  }

  if (kind === "skill") {
    return Puzzle;
  }

  return Bot;
}

function ComposerTokenNodeView({
  node,
}: {
  node: { attrs: ComposerTokenAttrs };
}) {
  const attrs = node.attrs;
  const Icon = getTokenIcon(attrs.icon, attrs.kind);

  return (
    <NodeViewWrapper
      as="span"
      className="composer-token"
      contentEditable={false}
      data-kind={attrs.kind}
    >
      <Icon className="h-3 w-3" />
      <span>{attrs.label}</span>
    </NodeViewWrapper>
  );
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    composerToken: {
      /**
       * Inserts an inline token into the editor at the current selection.
       * A trailing space is added so the user can continue typing naturally.
       */
      insertComposerToken: (attrs: ComposerTokenAttrs) => ReturnType;
    };
  }
}

/**
 * Inline atomic token node for the chat composer.
 * Provides the editor-level token behavior the textarea approach could not support.
 */
export const ComposerTokenNode = Node.create({
  name: "composerToken",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      kind: { default: "agent" },
      id: { default: null },
      label: { default: "" },
      value: { default: null },
      icon: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-composer-token]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-composer-token": "true",
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ComposerTokenNodeView);
  },

  addCommands() {
    return {
      insertComposerToken:
        (attrs: ComposerTokenAttrs) =>
        ({ chain }) =>
          chain()
            .insertContent([
              { type: this.name, attrs },
              { type: "text", text: " " },
            ])
            .run(),
    };
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () => {
        const { empty, $from } = this.editor.state.selection;
        if (!empty) {
          return false;
        }

        const nodeBefore = $from.nodeBefore;
        if (!nodeBefore || nodeBefore.type.name !== this.name) {
          return false;
        }

        const from = $from.pos - nodeBefore.nodeSize;
        const to = $from.pos;

        return this.editor.commands.command(({ tr }) => {
          tr.delete(from, to);
          return true;
        });
      },
      Delete: () => {
        const { empty, $from } = this.editor.state.selection;
        if (!empty) {
          return false;
        }

        const nodeAfter = $from.nodeAfter;
        if (!nodeAfter || nodeAfter.type.name !== this.name) {
          return false;
        }

        const from = $from.pos;
        const to = $from.pos + nodeAfter.nodeSize;

        return this.editor.commands.command(({ tr }) => {
          tr.delete(from, to);
          return true;
        });
      },
    };
  },
});
